import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AutoComplete, Input, Select, message } from "antd";
import { RcFile } from "antd/es/upload/interface";

import { AddressSuggestion, fetchAddressSuggestions, PropertyFormOutput } from "components/property-form/property-form";

import { useFileUpload } from "hooks/useFileUpload";
import { readAsDataUrl } from "utils/files-management";
import {
	BUILDING_TYPE_OPTIONS,
	OCCUPANCY_CLASS_OPTIONS,
	CONSTRUCTION_TYPE_OPTIONS,
	FIRE_SUPPRESSION_OPTIONS
} from "constants/property-field-options";

const ICONS_PATH = `${process.env.PUBLIC_URL}/assets/images/icons`;

interface AddPropertyModalProps {
	/** Draft to edit; leave empty to start from a blank form. */
	initialValues?: PropertyFormOutput | null;
	organizationId: string;
	onCancel: () => void;
	onSubmit: (output: PropertyFormOutput) => void;
}

const digitsOnly = (value: string, maxLength: number) => value.replace(/\D/g, "").slice(0, maxLength);

/**
 * Short "Add New Property" form: only the project name and the address are required.
 * The rest of the property fields are optional and stay hidden until asked for.
 * Mount it only while it is shown: the form starts from `initialValues` on mount.
 */
const AddPropertyModal = ({ initialValues, organizationId, onCancel, onSubmit }: AddPropertyModalProps) => {
	const [name, setName] = useState(initialValues?.name ?? "");
	const [address, setAddress] = useState(initialValues?.address ?? "");
	const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
	const [selectedAddressComponents, setSelectedAddressComponents] = useState<
		PropertyFormOutput["selectedAddressComponents"]
	>(initialValues?.selectedAddressComponents ?? null);

	const [photoFile, setPhotoFile] = useState<RcFile | null>(initialValues?.propertyPhotoFile ?? null);
	const [photoThumbUrl, setPhotoThumbUrl] = useState(initialValues?.existingPhotoUrl ?? "");
	const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
	const photoInputRef = useRef<HTMLInputElement>(null);
	const { convertHeicIfNeeded } = useFileUpload();

	const [areDetailsOpen, setAreDetailsOpen] = useState(false);
	const [constructionType, setConstructionType] = useState(initialValues?.constructionType ?? "");
	const [occupancyClass, setOccupancyClass] = useState(initialValues?.occupancyClass ?? "");
	const [buildingType, setBuildingType] = useState(initialValues?.buildingType ?? "");
	const [numberOfStories, setNumberOfStories] = useState(initialValues?.numberOfStories ?? "");
	const [fireSuppression, setFireSuppression] = useState(initialValues?.fireSuppression ?? "");
	const [yearBuilt, setYearBuilt] = useState(initialValues?.yearBuilt ?? "");
	const [contactName, setContactName] = useState(initialValues?.contactName ?? "");
	const [contactPhoneNumber, setContactPhoneNumber] = useState(initialValues?.contactPhoneNumber ?? "");
	const [contactEmail, setContactEmail] = useState(initialValues?.contactEmail ?? "");

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") onCancel();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [onCancel]);

	const addressOptions = useMemo(() => addressSuggestions.map(s => ({ value: s.value })), [addressSuggestions]);

	const handleAddressSearch = useCallback(async (searchText: string) => {
		if (searchText.length < 3) {
			setAddressSuggestions([]);
			return;
		}
		try {
			setAddressSuggestions(await fetchAddressSuggestions(searchText));
		} catch (error) {
			console.error("Smarty address autocomplete failed", error);
			setAddressSuggestions([]);
		}
	}, []);

	const handleAddressSelect = useCallback(
		(value: string) => {
			const match = addressSuggestions.find(s => s.value === value);
			if (!match) return;
			setSelectedAddressComponents({
				street_1: match.street_1,
				street_2: match.street_2,
				city: match.city,
				state: match.state,
				postal_code: match.postal_code
			});
		},
		[addressSuggestions]
	);

	const handleAddressChange = useCallback(
		(value: string) => {
			setAddress(value);
			// Typing after picking a suggestion makes the structured address stale.
			if (!addressSuggestions.some(s => s.value === value)) setSelectedAddressComponents(null);
			if (!value) setAddressSuggestions([]);
		},
		[addressSuggestions]
	);

	const handlePhotoSelected = useCallback(
		async (file: File | undefined) => {
			if (!file) return;
			const isImage = file.type.startsWith("image/") || /\.(heic|heif)$/i.test(file.name);
			if (!isImage) {
				message.error("Please choose an image file.");
				return;
			}
			const converted = await convertHeicIfNeeded(file as RcFile);
			if (!converted) return;
			setPhotoFile(converted);
			setPhotoThumbUrl(await readAsDataUrl(converted));
		},
		[convertHeicIfNeeded]
	);

	const handleRemovePhoto = useCallback(() => {
		setPhotoFile(null);
		setPhotoThumbUrl("");
		if (photoInputRef.current) photoInputRef.current.value = "";
	}, []);

	const canSave = !!name.trim() && !!address.trim();

	const handleSave = useCallback(() => {
		if (!canSave) return;
		onSubmit({
			name: name.trim(),
			address: address.trim(),
			selectedAddressComponents,
			buildingType,
			occupancyClass,
			numberOfStories,
			numberOfUnits: "",
			constructionType,
			fireSuppression,
			yearBuilt,
			contactName: contactName.trim(),
			contactPhoneNumber: contactPhoneNumber.trim(),
			contactEmail: contactEmail.trim(),
			isPremium: false,
			expectedDeviceTypes: [],
			warrantyDateStart: null,
			warrantyDateEnd: null,
			selectedUsers: [],
			organizationId,
			propertyPhotoFile: photoFile,
			// A new file is uploaded by the parent. Without a new file, keep the photo URL that is already saved.
			existingPhotoUrl: photoFile ? "" : photoThumbUrl,
			drawingsFiles: [],
			floorPlanFiles: [],
			documentsFiles: [],
			warrantyDocFile: null
		});
	}, [
		canSave,
		onSubmit,
		name,
		address,
		selectedAddressComponents,
		buildingType,
		occupancyClass,
		numberOfStories,
		constructionType,
		fireSuppression,
		yearBuilt,
		contactName,
		contactPhoneNumber,
		contactEmail,
		organizationId,
		photoFile,
		photoThumbUrl
	]);

	const renderSelect = (
		label: string,
		value: string,
		options: { value: string; label: string }[],
		onChange: (value: string) => void
	) => (
		<div className="apm-field">
			<span className="apm-label">{label}</span>
			<Select
				className="apm-select"
				popupClassName="apm-select-popup"
				popupMatchSelectWidth={false}
				placeholder="Select..."
				value={value || undefined}
				options={options}
				onChange={onChange}
				suffixIcon={<img src={`${ICONS_PATH}/add-property-select-chevron.svg`} alt="" width={10} height={6} />}
			/>
		</div>
	);

	return createPortal(
		<div className="apm-overlay">
			<div className="apm-modal" role="dialog" aria-modal="true" aria-labelledby="apm-title">
				<div className="apm-header">
					<div className="apm-title-row">
						<img src={`${ICONS_PATH}/add-property-building.svg`} alt="" width={20} height={20} />
						<h3 className="apm-title" id="apm-title">
							Add New Property
						</h3>
					</div>
					<button type="button" className="apm-close" onClick={onCancel} aria-label="Close">
						<img src={`${ICONS_PATH}/add-property-close.svg`} alt="" width={20} height={20} />
					</button>
				</div>

				<div className="apm-body">
					<div className="apm-photo-row">
						<button
							type="button"
							className={`apm-photo-tile ${photoThumbUrl ? "apm-photo-tile-filled" : ""} ${isDraggingPhoto ? "apm-photo-tile-dragging" : ""}`}
							onClick={() => photoInputRef.current?.click()}
							onDragOver={event => {
								event.preventDefault();
								setIsDraggingPhoto(true);
							}}
							onDragLeave={() => setIsDraggingPhoto(false)}
							onDrop={event => {
								event.preventDefault();
								setIsDraggingPhoto(false);
								void handlePhotoSelected(event.dataTransfer.files[0]);
							}}
							aria-label={photoThumbUrl ? "Change property photo" : "Add property photo"}
						>
							{photoThumbUrl ? (
								<img className="apm-photo-image" src={photoThumbUrl} alt="Property preview" />
							) : (
								<img src={`${ICONS_PATH}/add-property-camera.svg`} alt="" width={24} height={24} />
							)}
						</button>
						<input
							ref={photoInputRef}
							type="file"
							accept="image/*,.heic,.heif"
							hidden
							onChange={event => {
								void handlePhotoSelected(event.target.files?.[0]);
								event.target.value = "";
							}}
						/>
						<div className="apm-photo-text">
							<span className="apm-photo-title">Property Photo</span>
							<span className="apm-photo-hint">Optional</span>
							{photoThumbUrl && (
								<button type="button" className="apm-photo-remove" onClick={handleRemovePhoto}>
									Remove photo
								</button>
							)}
						</div>
					</div>

					<div className="apm-field apm-field-spaced">
						<label className="apm-label" htmlFor="apm-name">
							Project Name *
						</label>
						<Input
							id="apm-name"
							className="apm-input apm-input-required"
							placeholder="e.g. The Domain Northside"
							value={name}
							autoComplete="off"
							onChange={e => setName(e.target.value)}
						/>
					</div>

					<div className="apm-field apm-field-spaced">
						<label className="apm-label" htmlFor="apm-address">
							Street Address *
						</label>
						<div className="apm-address-wrap">
							<AutoComplete
								id="apm-address"
								className="apm-address"
								options={addressOptions}
								value={address}
								onSearch={handleAddressSearch}
								onSelect={handleAddressSelect}
								onChange={handleAddressChange}
								popupClassName="apm-select-popup"
							>
								<Input
									className="apm-input apm-input-required apm-input-with-icon"
									placeholder="123 Example Way, Austin, TX 78731"
									autoComplete="off"
								/>
							</AutoComplete>
							<img
								className="apm-address-icon"
								src={`${ICONS_PATH}/add-property-map-pin.svg`}
								alt=""
								width={16}
								height={16}
							/>
						</div>
					</div>

					<button type="button" className="apm-toggle" onClick={() => setAreDetailsOpen(isOpen => !isOpen)}>
						<img
							src={`${ICONS_PATH}/${areDetailsOpen ? "add-property-chevron-up" : "add-property-chevron-down"}.svg`}
							alt=""
							width={16}
							height={16}
						/>
						<span>{areDetailsOpen ? "Hide optional details" : "Add optional property details"}</span>
					</button>

					{areDetailsOpen && (
						<div className="apm-details">
							<div className="apm-grid">
								{renderSelect("Construction Type", constructionType, CONSTRUCTION_TYPE_OPTIONS, setConstructionType)}
								{renderSelect("Occupancy Class", occupancyClass, OCCUPANCY_CLASS_OPTIONS, setOccupancyClass)}
								{renderSelect("Property Type", buildingType, BUILDING_TYPE_OPTIONS, setBuildingType)}
								<div className="apm-field">
									<label className="apm-label" htmlFor="apm-stories">
										Number of Stories
									</label>
									<Input
										id="apm-stories"
										className="apm-input"
										placeholder="e.g. 4"
										inputMode="numeric"
										value={numberOfStories}
										autoComplete="off"
										onChange={e => setNumberOfStories(digitsOnly(e.target.value, 3))}
									/>
								</div>
								{renderSelect("Fire Suppression System", fireSuppression, FIRE_SUPPRESSION_OPTIONS, setFireSuppression)}
								<div className="apm-field">
									<label className="apm-label" htmlFor="apm-year">
										Year Built
									</label>
									<Input
										id="apm-year"
										className="apm-input"
										placeholder="e.g. 2024"
										inputMode="numeric"
										value={yearBuilt}
										autoComplete="off"
										onChange={e => setYearBuilt(digitsOnly(e.target.value, 4))}
									/>
								</div>
							</div>

							<div className="apm-contact-divider">
								<span className="apm-contact-line" />
								<span className="apm-contact-title">Primary Contact</span>
								<span className="apm-contact-line" />
							</div>

							<div className="apm-contact">
								<div className="apm-field">
									<label className="apm-label" htmlFor="apm-contact-name">
										Full Name
									</label>
									<Input
										id="apm-contact-name"
										className="apm-input"
										placeholder="e.g. Jane Doe"
										value={contactName}
										autoComplete="off"
										onChange={e => setContactName(e.target.value)}
									/>
								</div>
								<div className="apm-grid">
									<div className="apm-field">
										<label className="apm-label" htmlFor="apm-contact-phone">
											Phone Number
										</label>
										<Input
											id="apm-contact-phone"
											className="apm-input"
											placeholder="(555) 123-4567"
											value={contactPhoneNumber}
											autoComplete="off"
											onChange={e => setContactPhoneNumber(e.target.value)}
										/>
									</div>
									<div className="apm-field">
										<label className="apm-label" htmlFor="apm-contact-email">
											Email
										</label>
										<Input
											id="apm-contact-email"
											className="apm-input"
											placeholder="jane@example.com"
											value={contactEmail}
											autoComplete="off"
											onChange={e => setContactEmail(e.target.value)}
										/>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>

				<div className="apm-footer">
					<button type="button" className="apm-btn apm-btn-cancel" onClick={onCancel}>
						Cancel
					</button>
					<button type="button" className="apm-btn apm-btn-save" disabled={!canSave} onClick={handleSave}>
						Save Property
					</button>
				</div>
			</div>
		</div>,
		document.body
	);
};

export default AddPropertyModal;
